/**
 * Масштаб сетки плана.
 *
 * Модель всегда дневная: колонка — рабочий день, операции и сохранение работают
 * в днях. Масштаб задаёт только «слот» — шаг сетки, к которому липнут жесты,
 * и раскладку по горизонтали. Поэтому переключение день/неделя мгновенное:
 * данные не перезагружаются, строки не пересобираются.
 *
 * Недельная шкала строится по календарю, а не по рабочим дням: от понедельника
 * недели, в которую попало первое число первого месяца, до воскресенья недели
 * с последним числом последнего месяца. Отсюда ровно столько колонок, сколько
 * семидневок в диапазоне, и все они одной ширины — неделя с праздником и даже
 * неделя без единого рабочего дня занимают столько же места, сколько полная.
 * Так устроены недельные шкалы в планировщиках (Bryntum, MS Project): шкала
 * равномерная, поэтому по ней можно вести взглядом и мерить сроки.
 *
 * Неделя на стыке месяцев режется границей месяца на две ячейки («31» в августе
 * и «01–04» в сентябре): день никогда не попадает в чужой месяц, а месяц состоит
 * из целого числа ячеек. Обе половинки занимают полную ширину — шкала остаётся
 * равномерной, просто дни внутри неполной ячейки шире.
 */

import { SORTABLE_DATE_FORMAT } from "modules/common/constants";
import moment from "moment";
import { PlanColumn, PlanPeriod } from "./plan-types";

export type PlanScale = "day" | "week";

/** Ширина колонки-дня в дневном масштабе, px — чуть уже ячейки табеля */
export const DAY_CELL_WIDTH = 30;

/** Ширина недели в недельном масштабе, px — под подпись вида «03–07» */
export const WEEK_SLOT_WIDTH = 46;

/** Единица сетки: день или неделя целиком */
export interface PlanSlot {
    /** Индекс первой колонки слота; -1 у недели без рабочих дней */
    start: number;
    /** Сколько рабочих дней в слоте */
    length: number;
    /** Подпись в шапке: «12» или «03–07» */
    label: string;
    title: string;
    /** Слот целиком в прошлом */
    isPast: boolean;
    /** Внутри слота есть сегодняшний день — подсвечивается весь слот */
    isToday: boolean;
    /** Неделя без рабочих дней: в неё нельзя ничего поставить */
    isEmpty: boolean;
    /** Месяц, к которому отнесён слот */
    periodIndex: number;
    /** Левый край и ширина, px */
    left: number;
    width: number;
}

/**
 * Раскладка сетки: слоты, обратный индекс «колонка → слот» и таблица офсетов.
 *
 * Офсеты нужны потому, что в недельном масштабе ширина дня зависит от того,
 * сколько рабочих дней в его неделе — линейной формулы «индекс × ширина» уже нет.
 */
export interface PlanGrid {
    scale: PlanScale;
    slots: PlanSlot[];
    /** Индекс слота для каждой колонки */
    slotOf: number[];
    /** Левый край каждой колонки, px */
    offsets: number[];
    /** Ширина каждой колонки, px */
    widths: number[];
    /** Ширина одного слота, px — шаг фоновой сетки */
    slotWidth: number;
    /** Полная ширина сетки, px */
    totalWidth: number;
}

/** Месяц, выровненный по границам недель */
export interface AlignedPeriod {
    period: PlanPeriod;
    firstColumn: number;
    lastColumn: number;
    left: number;
    width: number;
}

const MONTH_NAMES = [
    "января",
    "февраля",
    "марта",
    "апреля",
    "мая",
    "июня",
    "июля",
    "августа",
    "сентября",
    "октября",
    "ноября",
    "декабря",
];

const dayTitle = (column: PlanColumn) =>
    `${+column.dayOfMonth} ${MONTH_NAMES[column.month - 1] || ""} ${column.year}`;

const EMPTY_GRID: PlanGrid = {
    scale: "day",
    slots: [],
    slotOf: [],
    offsets: [],
    widths: [],
    slotWidth: DAY_CELL_WIDTH,
    totalWidth: 0,
};

export function buildGrid(
    columns: PlanColumn[],
    scale: PlanScale,
    periods: PlanPeriod[] = [],
    today: moment.Moment = moment().startOf("day")
): PlanGrid {
    if (!columns.length) {
        return { ...EMPTY_GRID, scale, slotWidth: scale === "week" ? WEEK_SLOT_WIDTH : DAY_CELL_WIDTH };
    }

    const slots = scale === "week" ? weekSlots(columns, periods, today) : daySlots(columns);
    const slotOf: number[] = new Array(columns.length).fill(0);
    const offsets: number[] = new Array(columns.length).fill(0);
    const widths: number[] = new Array(columns.length).fill(0);

    const slotWidth = scale === "week" ? WEEK_SLOT_WIDTH : DAY_CELL_WIDTH;

    slots.forEach((slot, index) => {
        slot.left = index * slotWidth;
        slot.width = slotWidth;

        if (slot.isEmpty) {
            return;
        }

        // дни делят слот поровну: границы недель остаются ровными при любой длине
        const dayWidth = slotWidth / slot.length;

        for (let i = 0; i < slot.length; i++) {
            const column = slot.start + i;

            slotOf[column] = index;
            offsets[column] = slot.left + i * dayWidth;
            widths[column] = dayWidth;
        }
    });

    return {
        scale,
        slots,
        slotOf,
        offsets,
        widths,
        slotWidth,
        totalWidth: slots.length * slotWidth,
    };
}

function daySlots(columns: PlanColumn[]): PlanSlot[] {
    return columns.map((column, index) => ({
        start: index,
        length: 1,
        label: column.dayOfMonth,
        title: dayTitle(column),
        isPast: column.isPast,
        isToday: column.isToday,
        isEmpty: false,
        periodIndex: column.periodIndex,
        left: 0,
        width: 0,
    }));
}

/**
 * Календарная нарезка на недели, порезанные границами месяцев.
 *
 * Неделя на стыке даёт две ячейки — «31» в августе и «01–04» в сентябре, — поэтому
 * ни один день не попадает в чужой месяц, а месяц состоит из целого числа ячеек.
 * Ширина у всех ячеек одинаковая: неполная неделя занимает столько же места,
 * сколько полная, а её дни просто шире.
 */
function weekSlots(columns: PlanColumn[], periods: PlanPeriod[], today: moment.Moment): PlanSlot[] {
    const months = periods.length ? periods : monthsOf(columns);
    const slots: PlanSlot[] = [];
    /** Календарные границы сегментов — по ним раскладываются колонки */
    const bounds: Array<{ from: number; to: number; full: boolean }> = [];

    months.forEach((month, periodIndex) => {
        const monthStart = moment({ year: month.year, month: month.month - 1, day: 1 });
        const monthEnd = monthStart.clone().endOf("month");
        let cursor = monthStart.clone();

        while (cursor.isSameOrBefore(monthEnd, "day")) {
            const weekEnd = cursor.clone().endOf("isoWeek");
            const segmentEnd = weekEnd.isAfter(monthEnd, "day") ? monthEnd.clone() : weekEnd;
            const full = cursor.isoWeekday() === 1 && segmentEnd.isoWeekday() === 7;

            bounds.push({
                from: +cursor.clone().startOf("day"),
                to: +segmentEnd.clone().endOf("day"),
                full,
            });

            slots.push({
                start: -1,
                length: 0,
                // пока рабочих дней нет — показываем календарный отрезок сегмента
                label: rangeLabel(cursor, segmentEnd),
                title: `${cursor.isoWeek()} неделя: рабочих дней нет`,
                isPast: segmentEnd.isBefore(today),
                isToday: today.isSameOrAfter(cursor, "day") && today.isSameOrBefore(segmentEnd, "day"),
                isEmpty: true,
                periodIndex,
                left: 0,
                width: 0,
            });

            cursor = segmentEnd.clone().add(1, "day").startOf("day");
        }
    });

    columns.forEach((column, columnIndex) => {
        const date = moment(column.day, SORTABLE_DATE_FORMAT);
        const stamp = +date;

        let index = bounds.findIndex((bound) => stamp >= bound.from && stamp <= bound.to);

        // день вне месяцев диапазона быть не должен, но терять его нельзя
        if (index < 0) {
            index = stamp < bounds[0].from ? 0 : bounds.length - 1;
        }

        const slot = slots[index];

        if (slot.isEmpty) {
            slot.isEmpty = false;
            slot.start = columnIndex;
            slot.length = 1;
            slot.label = column.dayOfMonth;
            slot.title = `${date.isoWeek()} неделя: ${dayTitle(column)}`;

            return;
        }

        slot.length += 1;
        slot.label = `${columns[slot.start].dayOfMonth}–${column.dayOfMonth}`;
        slot.title = `${date.isoWeek()} неделя: ${dayTitle(columns[slot.start])} — ${dayTitle(column)}`;
    });

    slots.forEach((slot) => {
        if (slot.isEmpty) {
            return;
        }

        const own = columns.slice(slot.start, slot.start + slot.length);

        // приглушаем ячейку, только когда прошли все её рабочие дни
        slot.isPast = own.every((column) => column.isPast);
        slot.isToday = slot.isToday || own.some((column) => column.isToday);
    });

    // Пустая полная неделя (новогодние праздники) в шкале нужна — она держит ритм.
    // А пустой огрызок недели на краю месяца это просто выходные: он только мешает.
    return slots.filter((slot, index) => !slot.isEmpty || bounds[index].full);
}

/** «03–07», «31» — календарный отрезок сегмента по рабочим дням недели */
function rangeLabel(from: moment.Moment, to: moment.Moment): string {
    const left = from.format("DD");
    // суббота и воскресенье в сетке не показываются, поэтому и в подписи их нет
    const right = to.isoWeekday() > 5 ? from.clone().isoWeekday(5) : to;

    return right.isSameOrBefore(from, "day") ? left : `${left}–${right.format("DD")}`;
}

/** Месяцы, встречающиеся в колонках — запасной вариант, когда периоды не переданы */
function monthsOf(columns: PlanColumn[]): PlanPeriod[] {
    const result: PlanPeriod[] = [];

    columns.forEach((column) => {
        const last = result[result.length - 1];

        if (last && last.month === column.month && last.year === column.year) {
            return;
        }

        result.push({
            month: column.month,
            year: column.year,
            name: "",
            workDaysCount: 0,
            workHours: 0,
            firstColumn: 0,
            lastColumn: 0,
        });
    });

    return result;
}

const slotAt = (grid: PlanGrid, column: number): PlanSlot | undefined => {
    if (!grid.slotOf.length) {
        return undefined;
    }

    const clamped = Math.max(0, Math.min(column, grid.slotOf.length - 1));

    return grid.slots[grid.slotOf[clamped]];
};

/** Первая колонка слота, которому принадлежит колонка */
export function snapStart(grid: PlanGrid, column: number): number {
    const slot = slotAt(grid, column);
    return slot && !slot.isEmpty ? slot.start : column;
}

/** Последняя колонка слота, которому принадлежит колонка */
export function snapEnd(grid: PlanGrid, column: number): number {
    const slot = slotAt(grid, column);
    return slot && !slot.isEmpty ? slot.start + slot.length - 1 : column;
}

/** Диапазон колонок слота — им заполняется целая неделя по двойному клику */
export function slotRange(grid: PlanGrid, column: number): { start: number; length: number } {
    const slot = slotAt(grid, column);

    return slot && !slot.isEmpty ? { start: slot.start, length: slot.length } : { start: column, length: 1 };
}

/** Индекс слота, которому принадлежит колонка; -1 — колонки нет или слот пуст */
export function slotIndexAt(grid: PlanGrid, column: number): number {
    if (column < 0 || column >= grid.slotOf.length) {
        return -1;
    }

    const index = grid.slotOf[column];

    return grid.slots[index] && !grid.slots[index].isEmpty ? index : -1;
}

/**
 * Непустые слоты, пересекающиеся с диапазоном колонок.
 *
 * Единица переноса в недельном масштабе — ячейка, а не день: недели разной длины
 * (огрызок месяца, неделя с праздником) считаются одинаково.
 */
export function slotsOfRange(grid: PlanGrid, start: number, length: number): PlanSlot[] {
    if (length < 1) {
        return [];
    }

    const end = start + length - 1;

    return grid.slots.filter(
        (slot) => !slot.isEmpty && slot.start <= end && slot.start + slot.length - 1 >= start
    );
}

/**
 * `count` непустых слотов подряд, начиная со слота колонки `from`.
 * Пустые недели пропускаются: поставить в них нечего.
 * Возвращает null, если столько слотов до конца сетки не набирается.
 */
export function slotsFrom(grid: PlanGrid, from: number, count: number): PlanSlot[] | null {
    const first = slotIndexAt(grid, from);

    if (first < 0 || count < 1) {
        return null;
    }

    const result: PlanSlot[] = [];

    for (let index = first; index < grid.slots.length && result.length < count; index++) {
        if (!grid.slots[index].isEmpty) {
            result.push(grid.slots[index]);
        }
    }

    return result.length === count ? result : null;
}

/** Самая правая колонка, с которой ещё помещается `count` ячеек подряд */
export function lastSlotStart(grid: PlanGrid, count: number): number {
    const filled = grid.slots.filter((slot) => !slot.isEmpty);

    if (!filled.length) {
        return 0;
    }

    const index = Math.max(0, filled.length - Math.max(1, count));

    return filled[index].start;
}

/**
 * Колонки, которые займут `count` ячеек, начиная с ячейки колонки `from`.
 * Если ячеек до конца сетки не хватает, отдаёт то, что есть — этим рисуется
 * призрак приземления, а решение «не помещается» принимает уже операция.
 */
export function slotsSpan(grid: PlanGrid, from: number, count: number): { start: number; length: number } {
    const slots = slotsFrom(grid, from, count) || slotsOfRange(grid, from, grid.slotOf.length - from);

    if (!slots.length) {
        return { start: from, length: count };
    }

    const last = slots[slots.length - 1];

    return { start: slots[0].start, length: last.start + last.length - slots[0].start };
}

/** Левый край колонки, px */
export function columnLeft(grid: PlanGrid, column: number): number {
    if (!grid.offsets.length) {
        return 0;
    }

    const last = grid.offsets.length - 1;

    // за последней колонкой могут стоять пустые недели, поэтому конец сетки
    // и правый край последнего рабочего дня — разные точки
    if (column > last) {
        return grid.offsets[last] + grid.widths[last];
    }

    return grid.offsets[Math.max(0, column)];
}

/** Ширина отрезка колонок, px */
export function columnsWidth(grid: PlanGrid, start: number, length: number): number {
    if (length <= 0 || !grid.offsets.length) {
        return 0;
    }

    const from = Math.max(0, start);
    const to = Math.min(start + length - 1, grid.offsets.length - 1);

    if (to < from) {
        return 0;
    }

    return grid.offsets[to] + grid.widths[to] - grid.offsets[from];
}

/**
 * Колонка под точкой (координата в логических px сетки).
 * Возвращает -1 над неделей без рабочих дней — там ничего сделать нельзя.
 */
export function columnAtOffset(grid: PlanGrid, x: number): number {
    if (!grid.slots.length) {
        return -1;
    }

    const index = Math.max(0, Math.min(Math.floor(x / grid.slotWidth), grid.slots.length - 1));
    const slot = grid.slots[index];

    if (slot.isEmpty) {
        return -1;
    }

    const dayWidth = slot.width / slot.length;
    const inside = Math.floor((x - slot.left) / dayWidth);

    return slot.start + Math.max(0, Math.min(inside, slot.length - 1));
}

/**
 * Месяцы, выровненные по границам слотов: в недельном масштабе месяц занимает
 * целое число недель, поэтому граница не разрезает ячейку посередине.
 */
export function alignPeriods(periods: PlanPeriod[], grid: PlanGrid): AlignedPeriod[] {
    const result: AlignedPeriod[] = [];

    periods.forEach((period, index) => {
        const own = grid.slots.filter((slot) => slot.periodIndex === index);

        if (!own.length) {
            return;
        }

        const first = own[0];
        const last = own[own.length - 1];
        const filled = own.filter((slot) => !slot.isEmpty);

        const lastFilled = filled[filled.length - 1];

        result.push({
            period,
            firstColumn: filled.length ? filled[0].start : -1,
            lastColumn: lastFilled ? lastFilled.start + lastFilled.length - 1 : -1,
            left: first.left,
            width: last.left + last.width - first.left,
        });
    });

    return result;
}
